import { api } from './client';
import type { Page } from './catalogs';

/** Кабінет звітності: журнал відправок електронних документів у ДПС. */

export type DpsStatus =
  'DRAFT' | 'SIGNED' | 'SENT' | 'DELIVERED' | 'ACCEPTED' | 'REJECTED' | 'ERROR';

export interface DpsSubmission {
  id: string;
  organization_id: string;
  source_type: string;
  source_id: string;
  form_code: string;
  form_title: string | null;
  // Період подання (як у веб-кабінеті ДПС): 1=місяць, 2=квартал.
  period_year: number | null;
  period_month: number | null;
  period_type: number | null;
  file_name: string;
  status: DpsStatus;
  dps_report_id: number | null;
  dps_status_code: string | null;
  error_message: string | null;
  kvt1_base64: string | null;
  kvt1_fname: string | null;
  kvt2_base64: string | null;
  kvt2_fname: string | null;
  kvt2_is_final: boolean;
  receipt_text: string | null;
  receipt_accepted: boolean | null;
  receipt1_xml: string | null;
  receipt2_xml: string | null;
  signer_edrpou: string | null;
  sent_at: string | null;
  delivered_at: string | null;
  finalized_at: string | null;
  created_at: string;
}

export interface DpsSubmissionCreate {
  organization_id: string;
  source_type: string;
  source_id: string;
  form_code: string;
  form_title?: string | null;
  file_name: string;
  container_base64: string;
  signer_edrpou?: string | null;
  signed_at?: string | null;
}

export interface DpsSubmitResult {
  submission: DpsSubmission;
  dps_status: string | null;
  message: string | null;
}

export const dpsSubmissions = {
  list: (params?: {
    organization_id?: string; source_type?: string; source_id?: string;
    status?: string; form_code?: string; form_title?: string;
    date_from?: string; date_to?: string;
    sort_by?: string; sort_dir?: 'asc' | 'desc';
    limit?: number; offset?: number;
  }) => api.get<Page<DpsSubmission>>('/dps-submissions', { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<DpsSubmission>(`/dps-submissions/${id}`).then((r) => r.data),
  submit: (p: DpsSubmissionCreate) =>
    api.post<DpsSubmitResult>('/dps-submissions', p).then((r) => r.data),
  sign: (id: string, containerBase64: string, fileName: string, signerEdrpou?: string) =>
    api.post<DpsSubmission>(`/dps-submissions/${id}/sign`, {
      container_base64: containerBase64, file_name: fileName,
      ...(signerEdrpou ? { signer_edrpou: signerEdrpou } : {}),
    }).then((r) => r.data),
  send: (id: string) =>
    api.post<DpsSubmitResult>(`/dps-submissions/${id}/send`).then((r) => r.data),
  signedContainer: (id: string) =>
    api.get<{ container_base64: string | null }>(`/dps-submissions/${id}/signed-container`).then((r) => r.data),
  refresh: (id: string, encryptedIdBase64?: string, signerEdrpou?: string) =>
    api.post<DpsSubmission>(`/dps-submissions/${id}/refresh`, {
      ...(encryptedIdBase64 ? { encrypted_id_base64: encryptedIdBase64 } : {}),
      ...(signerEdrpou ? { signer_edrpou: signerEdrpou } : {}),
    }).then((r) => r.data),
  saveReceiptText: (
    id: string, receiptText: string, accepted: boolean | null,
    receiptXml?: string, receiptNum?: 1 | 2,
  ) =>
    api.post<DpsSubmission>(`/dps-submissions/${id}/receipt-text`, {
      receipt_text: receiptText, receipt_accepted: accepted,
      ...(receiptXml ? { receipt_xml: receiptXml, receipt_num: receiptNum } : {}),
    }).then((r) => r.data),
  remove: (id: string) => api.delete(`/dps-submissions/${id}`).then(() => undefined),
};

/** Вхідний документ від ДПС (квитанція/витяг/відповідь на запит). */
export interface DpsIncoming {
  id: string;
  organization_id: string;
  submission_id: string;
  num_kvt: number;
  form_code: string | null;
  title: string | null;
  fname: string | null;
  doc_text: string | null;
  is_final: boolean;
  kvt_status: number;
  received_at: string;
  // Реквізити подачі — до якого нашого документа прийшла ця відповідь.
  submission_form_code: string | null;
  submission_form_title: string | null;
  submission_file_name: string | null;
}

export const dpsIncoming = {
  list: (params?: { organization_id?: string; submission_id?: string; limit?: number }) =>
    api.get<Page<DpsIncoming>>('/dps-submissions/incoming', { params }).then((r) => r.data),
  file: (id: string) =>
    api.get(`/dps-submissions/incoming/${id}/file`, { responseType: 'blob' }),
};

/** Email-сповіщення ДПС (вид документа + дата + посилання «дивитись»). */
export interface DpsEmailNotification {
  id: string;
  organization_id: string | null;
  recipient_code: string;
  subject: string | null;
  doc_kind: string | null;
  view_link: string | null;
  doc_datetime: string | null;
  received_at: string;
}

export const dpsEmailNotifications = {
  list: (params?: { organization_id?: string; limit?: number }) =>
    api.get<Page<DpsEmailNotification>>('/dps-submissions/email-notifications',
      { params }).then((r) => r.data),
};

/** Вхідний документ приватного API ЕК (post/incoming). */
export interface DpsPrivIncoming {
  id: number;
  cdoc: string | null;
  tin: number | null;
  dateIn: string | null;
  periodYear: number | null;
  isRead: number | null;
  status: number | null;
  text?: string | null;       // назва/зміст документа
  docName?: string | null;    // людська назва форми (якщо є)
}

export const dpsPrivApi = {
  incoming: (p: { auth_header: string; organization_id: string;
    signer_edrpou?: string; page?: number }) =>
    api.post<{
      content: DpsPrivIncoming[]; totalElements?: number;
      totalPages?: number; number?: number; last?: boolean; first?: boolean;
    }>('/dps-submissions/priv/incoming', p).then((r) => r.data),
  file: (p: { auth_header: string; organization_id: string;
    signer_edrpou?: string; year: number; doc_id: string; fmt?: 'pdf' | 'xml' }) =>
    api.post('/dps-submissions/priv/incoming/file', p,
      { responseType: 'blob' }),
  // Забрати ВСІ вхідні з кабінету і зберегти у нас (дедуп). Повертає counts.
  sync: (p: { auth_header: string; organization_id: string; signer_edrpou?: string }) =>
    api.post<{ total_in_cabinet: number; saved: number; errors: number }>(
      '/dps-submissions/priv/incoming/sync', p).then((r) => r.data),
  // 2026-08-06: розділ кабінету «Реєстрація ПН-РК» — РК/ПН, які контрагент
  // передав нам на підпис і реєстрацію (b2b/docs, Bearer автологіну).
  b2bDocs: (p: { auth_header: string; organization_id: string;
    signer_edrpou?: string; page?: number }) =>
    api.post<{
      content: {
        cd: number; sndEdrpou: string | null; sndName: string | null;
        rcpEdrpou: string | null; docId: string | null;
        incomeDt: string | null; deliverDt: string | null;
      }[];
      totalElements?: number; totalPages?: number; number?: number;
      last?: boolean; first?: boolean;
    }>('/dps-submissions/priv/b2b-docs', p).then((r) => r.data),
  // Автологін кабінету (КЕП → Bearer, 2026-08-04): обмін підпису на токен
  // сесії для внутрішніх реєстрів /ws/api/* (ЄРПН). Токен живе 600 с.
  oauthToken: (p: { organization_id: string; signer_edrpou?: string;
    username: string; password: string }) =>
    api.post<{ access_token: string; token_type: string; expires_in: number }>(
      '/dps-submissions/priv/oauth-token', p).then((r) => r.data),
  // Журнал запусків «ПН → у систему» (вкладка «Отримати ПН», 2026-08-04).
  erpnFetchLogViewed: (organizationId: string) =>
    api.post('/tax-invoices-incoming/erpn-fetch-log/viewed', null,
      { params: { organization_id: organizationId } }),
  erpnFetchLog: (organizationId: string) =>
    api.get<{
      id: string; date_from: string; date_to: string;
      status: 'OK' | 'ERROR'; fetched: number; created: number;
      matched: number; skipped_existing: number;
      rk_to_sign?: number; issued_updated?: number;
      error_text: string | null; created_at: string;
      viewed_at?: string | null;
    }[]>('/tax-invoices-incoming/erpn-fetch-log',
      { params: { organization_id: organizationId } }).then((r) => r.data),
  // Отримані ПН з ЄРПН (nlnkhdcp) → створення вхідних ПН + авто-матчинг.
  erpnFetch: (p: { auth_header: string; organization_id: string;
    signer_edrpou: string; date_from: string; date_to: string }) =>
    api.post<{
      fetched: number; created: number; matched: number;
      skipped_existing: number; not_ours: string[]; no_contractor: string[];
      created_contractors?: string[];
      unmatched: string[]; errors: string[];
    }>('/tax-invoices-incoming/erpn-fetch', p).then((r) => r.data),
  // 2026-08-06: ЄДИНА синхронізація з ЄРПН за період (3 кроки однією
  // кнопкою): отримані ПН → у систему; непідписані РК → «на підпис»;
  // статуси виданих ПН/РК.
  erpnSyncAll: (p: { auth_header: string; organization_id: string;
    signer_edrpou: string; date_from: string; date_to: string }) =>
    api.post<{
      fetched?: number; created?: number; matched?: number;
      skipped_existing?: number; created_contractors?: string[];
      unmatched?: string[]; no_contractor?: string[]; not_ours?: string[];
      b2b_total?: number; rk_to_sign_created?: number; rk_already?: number;
      rk_skipped_signed?: number; rk_signed_now?: number;
      issued_updated?: number; already_registered?: number;
      not_in_register?: string[]; register_rows_preview?: string[];
      skipped_rows?: string[];
      errors: string[];
    }>('/tax-invoices-incoming/erpn-sync-all', p).then((r) => r.data),
  // 2026-08-06: звірка ВИДАНИХ ПН/РК з реєстром кабінету (nlnkhd) —
  // головний кейс: зменшуючий РК реєструє покупець, квитанції нам немає.
  erpnSyncIssued: (p: { auth_header: string; organization_id: string;
    signer_edrpou: string; date_from: string; date_to: string }) =>
    api.post<{
      fetched: number; updated: number; already_registered: number;
      not_in_register: string[]; register_rows_preview: string[];
      skipped_rows: string[];
    }>('/tax-invoices-outgoing/erpn-sync-issued', p).then((r) => r.data),
};

/** Збережений вхідний документ кабінету ДПС (з приватного API). */
export interface DpsCabinetDoc {
  id: string;
  organization_id: string;
  dps_doc_id: string;
  cdoc: string | null;
  title: string | null;
  doc_name: string | null;
  date_in: string | null;
  period_year: number | null;
  is_read: number;
  dps_status: number | null;
  fetched_at: string;
  viewed_at: string | null;
  has_pdf: boolean;
  // false → документ існує лише як ТЕКСТ у списку (файлу немає):
  // BOTB0501/BOTB0101/D0300201 тощо. Такий показуємо текстом.
  has_body?: boolean;
}

export const dpsCabinetDocs = {
  list: (params?: { organization_id?: string; limit?: number }) =>
    api.get<Page<DpsCabinetDoc>>('/dps-submissions/priv/documents', { params })
      .then((r) => r.data),
  // 0432: власна позначка «переглянуто» (жирність непереглянутих).
  markViewed: (id: string) =>
    api.post(`/dps-submissions/priv/documents/${id}/viewed`),
  pdf: (id: string) =>
    api.get(`/dps-submissions/priv/documents/${id}/pdf`, { responseType: 'blob' }),
};

/** Збережений на сервері ключ КЕП (за згодою юзера; пароль НЕ зберігається). */
export interface KepKeyMeta {
  id: string;
  file_name: string;
  subj_cn: string | null;
  subj_org: string | null;
  issuer_cn: string | null;
  serial: string;
  edrpou: string | null;
  drfo: string | null;
}

export const kepKeys = {
  list: () => api.get<KepKeyMeta[]>('/kep-keys').then((r) => r.data),
  save: (p: {
    file_name: string; key_base64: string; serial: string;
    subj_cn?: string | null; subj_org?: string | null; issuer_cn?: string | null;
    edrpou?: string | null; drfo?: string | null;
  }) => api.post<KepKeyMeta>('/kep-keys', p).then((r) => r.data),
  blob: (id: string) =>
    api.get<{ key_base64: string; file_name: string }>(`/kep-keys/${id}/blob`)
      .then((r) => r.data),
  remove: (id: string) => api.delete(`/kep-keys/${id}`).then(() => undefined),
};

/** Поданий звіт (reg_doc/list) + файли (reg_doc/docs). */
export interface DpsRegDoc {
  codRegdoc: number;
  doc: string | null;
  cdocSub: string | null;
  cdocVer?: number | null;
  periodMonth: number | null;
  periodYear: number | null;
  csti: number | null;
  docName?: string | null;   // людська назва форми (з API)
  dget?: string | null;      // дата отримання/подання
  dterm?: string | null;     // граничний термін
  nreg?: string | null;      // реєстраційний номер
  flagName?: string | null;  // стан (прийнято тощо)
  _viewed_at?: string | null; // 0432: наша позначка «переглянуто» (saved)
  _id?: string; // 0452: якір архіву вкладень «Кабінет ДПС» (перегляд без ключа)
  cntAppendix?: number | null;
}

// 0434: одна СПРОБА подання (кожна відправка — свій рядок з квитанцією).
export interface DpsAttempt {
  id: string;
  submission_id: string;
  form_code: string;
  form_title?: string | null;
  file_name: string;
  doc_num?: string | null;      // номер розрахунку з імені файла
  dps_report_id?: string | null;
  status: string;               // SENT | DELIVERED | ACCEPTED | REJECTED | ERROR
  error_message?: string | null;
  receipt_text?: string | null;
  receipt_accepted?: boolean | null;
  has_kvt1?: boolean;
  has_kvt2?: boolean;
  // 5 станів (0435): sent | accepted_dps | not_accepted |
  // report_accepted | report_rejected (+ error/draft)
  state?: string;
  state_label?: string;
  sent_at?: string | null;
}

/** Стан крипто-ресурсів ДПС (0437: строки дії сертифікатів). */
export interface EusignAssetsStatus {
  checked_at: string | null;
  iit_refreshed_at: string | null;
  certs: { name: string; not_after: string | null; days_left: number | null;
    expiring: boolean; expired: boolean }[];
}

export const eusignAssets = {
  status: () => api.get<EusignAssetsStatus>('/eusign/assets-status')
    .then((r) => r.data),
};

export const dpsRegDoc = {
  list: (p: { auth_header: string; organization_id: string; signer_edrpou?: string;
    period_year: number; period_month: number }) =>
    api.post<{ content: DpsRegDoc[]; totalElements?: number }>(
      '/dps-submissions/priv/reg-doc/list', p).then((r) => r.data),
  // 2026-08-07: ЗБЕРЕЖЕНИЙ список за період (з нашої БД, без ключа).
  saved: (p: { organization_id: string; period_year: number; period_month: number }) =>
    api.get<DpsRegDoc[]>('/dps-submissions/priv/reg-doc/saved', { params: p })
      .then((r) => r.data),
  // 0432: позначка «переглянуто» для рядка Звітності.
  markViewed: (p: { organization_id: string; cod_regdoc: string }) =>
    api.post('/dps-submissions/priv/reg-doc/viewed', null, { params: p }),
  // 0434: НАШІ подачі за період (журнал спроб). Реєстр кабінету показує
  // лише ПРИЙНЯТУ звітність — відхилений пакет туди не потрапляє.
  attempts: (p: { organization_id: string; period_year: number; period_month: number }) =>
    api.get<DpsAttempt[]>('/dps-submissions/priv/attempts', { params: p })
      .then((r) => r.data),
  // Файли одного звіту: основний + додатки + квитанції (кожен зі своїм codRegdoc).
  files: (p: { auth_header: string; organization_id: string; signer_edrpou?: string;
    year: number; cod_regdoc: string }) =>
    api.post<DpsRegDoc[]>('/dps-submissions/priv/reg-doc/files', p).then((r) => r.data),
  file: (p: { auth_header: string; organization_id: string; signer_edrpou?: string;
    year: number; cod_regdoc: string; fmt?: 'pdf' | 'xml';
    // 0452: якір архіву «Кабінет ДПС» (головний звіт) + людська назва файлу.
    anchor_cod_regdoc?: string; file_label?: string }) =>
    api.post('/dps-submissions/priv/reg-doc/file', p, { responseType: 'blob' }),
};
